import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import type api from 'api';
import type { HTTPError } from 'api/error';
import type {
  AuthorizeOpenTableOAuthRequest,
  CreateOpenTableIntegrationRequest,
  UpdateOpenTableIntegrationRequest,
} from 'api/resources/opentable/types';
import { useToast } from 'hooks/useToast';
import type { MutationOptionsFor } from '../types';

const useGetOpenTableIntegration = (
  restaurantId?: string,
  accountID?: string,
  token?: string,
) => {
  const api = useApi();
  const toast = useToast();
  const enabled = !!restaurantId && !!accountID && !!token;
  return useQuery({
    queryKey: ['OpenTableIntegration', 'get', restaurantId, accountID],
    enabled: enabled,
    queryFn: () =>
      api.methods.getOpenTableIntegration({
        restaurant_id: restaurantId!,
        account_id: accountID!,
        token: token!,
      }),
    onError: () => {
      toast.error({
        title: 'Failed to find OpenTable integration',
      });
    },
  });
};

interface UseCreateOpenTableIntegrationOptions {
  options?: MutationOptionsFor<
    typeof api.createOpenTableIntegration,
    HTTPError,
    CreateOpenTableIntegrationRequest
  >;
}

const useCreateOpenTableIntegration = ({
  options,
}: UseCreateOpenTableIntegrationOptions = {}) => {
  const api = useApi();
  const toast = useToast();
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (request: CreateOpenTableIntegrationRequest) =>
      api.methods.createOpenTableIntegration(request),
    onSuccess: (...args) => {
      // Handles 401 cases where user is not authorized and fails silently
      const [data] = args;
      if (!data?.status) {
        throw new Error('Failed to create OpenTable integration');
      }
      onSuccess?.(...args);
      toast.success({
        title: 'OpenTable integration created successfully',
      });
    },
    onError: (...args) => {
      onError?.(...args);
      toast.error({
        title: 'Failed to create OpenTable integration',
      });
    },
    ...restOptions,
  });
};

interface UseUpdateOpenTableIntegrationOptions {
  options?: MutationOptionsFor<
    typeof api.updateOpenTableIntegration,
    HTTPError,
    UpdateOpenTableIntegrationRequest
  >;
}

const useUpdateOpenTableIntegration = ({
  options,
}: UseUpdateOpenTableIntegrationOptions = {}) => {
  const api = useApi();
  const toast = useToast();
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (request: UpdateOpenTableIntegrationRequest) =>
      api.methods.updateOpenTableIntegration(request),
    onSuccess(...args) {
      // Handles 401 cases where user is not authorized and fails silently
      const [data] = args;
      if (!data?.status) {
        throw new Error('Failed to update OpenTable integration');
      }
      onSuccess?.(...args);
    },
    onError: (...args) => {
      onError?.(...args);
      toast.error({
        title: 'Failed to update OpenTable integration',
      });
    },
    ...restOptions,
  });
};

interface UseAuthorizeOpenTableOAuthOptions {
  options?: MutationOptionsFor<
    typeof api.authorizeOpenTableOAuth,
    HTTPError,
    AuthorizeOpenTableOAuthRequest
  >;
}

const useAuthorizeOpenTableOAuth = ({
  options,
}: UseAuthorizeOpenTableOAuthOptions = {}) => {
  const api = useApi();
  const toast = useToast();
  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (request: AuthorizeOpenTableOAuthRequest) =>
      api.methods.authorizeOpenTableOAuth(request),
    onSuccess: (...args) => {
      onSuccess?.(...args);
    },
    onError: (...args) => {
      onError?.(...args);
      toast.error({
        title: 'Failed to authorize OpenTable OAuth',
      });
    },
    ...restOptions,
  });
};

export {
  useAuthorizeOpenTableOAuth,
  useCreateOpenTableIntegration,
  useGetOpenTableIntegration,
  useUpdateOpenTableIntegration,
};
